import React, { useCallback, useEffect, useState } from "react";
import { Password } from "primereact/password";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { PhoneInput } from "react-international-phone";
import {
  sportConstants,
  socialMediaConstants,
  translationConstants,
  documentTypeConstants,
  fieldLengthConstants,
  Directions,
  globalConstants,
  Regex,
  timezoneConstants,
} from "../../../../constants";
import {
  appendMandatorySignAndTimezoneText,
  UTCWithTimezoneToISOString,
  formatDOB,
  isPhoneValid,
  mergeDateTimeToServerFormat,
} from "../../../../system";
import { centerActions } from "../../../../redux/actions";
import InputFieldWrapper from "../../../../shared/components/InputFieldWrapper/InputFieldWrapper";
import ReactDatePickerWrapper from "../../../../shared/components/ReactDatePickerWrapper/ReactDatePickerWrapper";
import FileUploader from "../../../../shared/components/UploadFile/FileUploader";
import moment from "moment";

const BasicTab = ({ goBack, onNext }) => {
  const { Mandatory_Character } = globalConstants;
  const methods = useForm();
  const dispatch = useDispatch();

  const { t } = useTranslation();

  const cities = useSelector((state) => state.LoginReducer.cities);
  const formState = useSelector((state) => state.centerReducer.formState);

  const direction = useSelector(
    (state) => state.LanguageChangeReducer.direction
  );

  const [socialMedia, setSocialMedia] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [imgValidationMessage, setImgValidationMessage] = useState("");
  const [validationErrorMessage, setValidationErrorMessage] = useState("");

  const {
    control,
    trigger,
    register,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = methods;

  const onSubmit = (dataToSend) => {
    dataToSend = {
      ...formState,
      ...dataToSend,
      cityId: Number(selectedCity),
      centerInfo: {
        ...dataToSend.centerInfo,
        tikTokURL: dataToSend.tiktok ? dataToSend.centerInfo.tikTokURL : "",
        twitterURL: dataToSend.twitter ? dataToSend.centerInfo.twitterURL : "",
        instagramURL: dataToSend.instagram
          ? dataToSend.centerInfo.instagramURL
          : "",
        facebookURL: dataToSend.facebook
          ? dataToSend.centerInfo.facebookURL
          : "",
      },
      userInfo: {
        ...dataToSend.userInfo,
        documentType: documentTypeConstants.NAFES,
        documentExpiryDateString: mergeDateTimeToServerFormat(
          dataToSend.documentExpiryDate,
          moment().set(timezoneConstants.twelveAM).toDate()
        ),
      },
    };

    dispatch(centerActions.setFormState(dataToSend));
    onNext();
  };

  const handleFileUpload = useCallback(
    (file, key) => {
      setValue(key, file, { shouldValidate: true });
    },
    [setValue]
  );

  useEffect(() => {
    if (formState) {
      setValue("userInfo.documents", formState.userInfo?.documents);
      setValue("documentExpiryDate", formState.documentExpiryDate);
      setValue("centerInfo.name", formState.centerInfo?.name);
      setValue("centerInfo.arabicName", formState.centerInfo?.arabicName);
      setValue("centerInfo.cityId", formState.centerInfo?.cityId);
      setValue("centerInfo.address", formState.centerInfo?.address);
      setValue("centerInfo.taxNumber", formState.centerInfo?.taxNumber);
      setSelectedCity(formState.centerInfo?.cityId);
      setValue("centerInfo.websiteURL", formState.centerInfo?.websiteURL);
      setValue("centerInfo.phoneNumber", formState.centerInfo?.phoneNumber);
      setValue("centerInfo.email", formState.centerInfo?.email);
      setValue("centerInfo.description", formState.centerInfo?.description);
      setValue(
        "centerInfo.arabicDescription",
        formState.centerInfo?.arabicDescription
      );
      setValue("sports", formState.sports);
      setValue("logo", formState.logo);
      setValue(socialMediaConstants.TIKTOK, formState.twitter);
      setValue("centerInfo.twitterURL", formState.centerInfo?.twitterURL);
      setValue(socialMediaConstants.INSTAGRAM, formState.instagram);
      setValue("centerInfo.instagramURL", formState.centerInfo?.instagramURL);
      setValue(socialMediaConstants.FACEBOOK, formState.facebook);
      setValue("centerInfo.facebookURL", formState.centerInfo?.facebookURL);
      setValue(socialMediaConstants.TIKTOK, formState.tiktok);
      setValue("centerInfo.tikTokURL", formState.centerInfo?.tikTokURL);
      setValue("userInfo.email", formState.userInfo?.email);
      setValue("userInfo.password", formState.userInfo?.password);
      setValue("confirmPassword", formState.confirmPassword);
    }
    // eslint-disable-next-line
  }, []);

  const onSocialMediaChange = (key) => {
    let list = [...socialMedia];
    if (list.includes(key)) list = list.filter((x) => x !== key);
    else list.push(key);

    setSocialMedia(list);
  };

  // validation, at least one sport checkbox needs to be ticked
  const atLeastOneSportChecked = (selectedMediums) => {
    return selectedMediums.length > 0;
  };

  return (
    <FormProvider {...methods}>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Container>
          <Row>
            <InputFieldWrapper
              controlId={"userInfo.documents"}
              label={
                `${t(translationConstants.UPLOAD_NAFES_APPROVAL)}:` +
                Mandatory_Character
              }
            >
              <div className="filterUploader">
                <FileUploader
                  sm
                  single
                  defaultValue={formState?.userInfo?.documents}
                  {...register("userInfo.documents", {
                    required: t(translationConstants.imageRequired),
                  })}
                  onFileUpload={(file) =>
                    handleFileUpload(file, "userInfo.documents")
                  }
                  setValidationErrorMessage={setValidationErrorMessage}
                  isError={errors.userInfo?.documents || validationErrorMessage}
                />
                {errors.userInfo?.documents && !validationErrorMessage && (
                  <p className="errorText">
                    {errors.userInfo.documents.message}
                  </p>
                )}
                {validationErrorMessage && (
                  <p className="errorText">{validationErrorMessage}</p>
                )}
              </div>
            </InputFieldWrapper>

            <InputFieldWrapper
              controlId={"documentExpiryDate"}
              label={appendMandatorySignAndTimezoneText(
                t(translationConstants.NAFAS_EXPIRY_DATE),
                t
              )}
            >
              <Controller
                name="documentExpiryDate"
                className="cursor-pointer"
                rules={{
                  required: t(translationConstants.expiryDateRequired),
                }}
                render={({ field }) => (
                  <ReactDatePickerWrapper
                    value={field.value}
                    selected={field.value}
                    minDate={new Date(UTCWithTimezoneToISOString())}
                    onChange={(date) => {
                      field.onChange(date);
                    }}
                  />
                )}
              />
              {errors.documentExpiryDate && (
                <p className="errorText">{errors.documentExpiryDate.message}</p>
              )}
            </InputFieldWrapper>

            <InputFieldWrapper
              label={
                `${t(translationConstants.CENTER_NAME)}:` + Mandatory_Character
              }
            >
              <Form.Control
                type="text"
                maxLength={fieldLengthConstants.TEXT_FIELD_LENGTH}
                id="centerInfo.name"
                placeholder={t(translationConstants.ENTER_CENTER_NAME)}
                {...register("centerInfo.name", {
                  required: t(translationConstants.CENTER_NAME_REQUIRED),
                  pattern: {
                    value:
                      /^[A-Za-z0-9\s!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]+$/i,
                    message: t(translationConstants.INVALID_CENTER_NAME),
                  },
                })}
              />
              {errors.centerInfo?.name && (
                <p className="errorText">{errors.centerInfo?.name.message}</p>
              )}
            </InputFieldWrapper>

            <InputFieldWrapper
              label={
                <>
                  {t(translationConstants.centerNameArabic)}:{" "}
                  <span className="greenColor">
                    {t(translationConstants.optional)}
                  </span>
                </>
              }
            >
              <Form.Control
                {...register("centerInfo.arabicName", {})}
                id="centerInfo.arabicName"
                dir={Directions.rtl}
                type="text"
                maxLength={fieldLengthConstants.TEXT_FIELD_LENGTH}
                placeholder={"أدخل إسم المركز"}
              />
              {errors.centerInfo?.arabicName && (
                <p className="errorText">
                  {errors.centerInfo?.arabicName.message}
                </p>
              )}
            </InputFieldWrapper>

            <InputFieldWrapper
              label={
                `${t(translationConstants.ENTER_CENTER_CITY)}:` +
                Mandatory_Character
              }
            >
              <Form.Select
                id="centerInfo.cityId"
                {...register("centerInfo.cityId", {
                  required: t(translationConstants.CENTER_CITY_REQUIRED),
                })}
                onChange={(e) => {
                  const value = e.target.value;
                  setValue("centerInfo.cityId", value, {
                    shouldValidate: true,
                  });
                  setSelectedCity(value);
                  trigger("centerInfo.cityId"); // Trigger validation for the 'countryresidence' field
                  e.stopPropagation();
                }}
              >
                <option value="">{t(translationConstants.selectcity)}</option>
                {cities?.map((city) => (
                  <option key={city.id} value={city.id}>
                    {direction === Directions.rtl ? city.arabicName : city.name}
                  </option>
                ))}
              </Form.Select>
              {errors.centerInfo?.cityId && (
                <p className="errorText">{errors.centerInfo.cityId.message}</p>
              )}
            </InputFieldWrapper>

            <InputFieldWrapper
              label={
                `${t(translationConstants.CENTER_ADDRESS)} ${t(
                  translationConstants.PREFERRED_ARABIC
                )}:` + Mandatory_Character
              }
            >
              <Form.Control
                type="text"
                id="centerInfo.address"
                placeholder={t(translationConstants.ENTER_CENTER_ADDRESS)}
                {...register("centerInfo.address", {
                  required: t(translationConstants.CENTER_ADDRESS_REQUIRED),
                })}
              />
              {errors.centerInfo?.address && (
                <p className="errorText">
                  {errors.centerInfo?.address.message}
                </p>
              )}
            </InputFieldWrapper>

            <InputFieldWrapper
              label={
                `${t(translationConstants.phoneNumber)}:` + Mandatory_Character
              }
            >
              <Controller
                name="centerInfo.phoneNumber"
                control={control}
                rules={{
                  required: t(translationConstants.phoneNumberRequired),
                  validate: (value) => {
                    const isValid = isPhoneValid(value);
                    if (!isValid) {
                      return t(translationConstants.enterValidPhoneNumber);
                    }
                    return true;
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <PhoneInput
                    id="phone-number-registration"
                    defaultCountry="sa"
                    value={value}
                    onChange={onChange}
                    maxLength={fieldLengthConstants.PHONENUMBER_FIELD_LENGTH}
                  />
                )}
              />
              {errors.centerInfo?.phoneNumber && (
                <p className="errorText">
                  {errors.centerInfo?.phoneNumber.message}
                </p>
              )}
            </InputFieldWrapper>

            <InputFieldWrapper
              label={
                `${t(translationConstants.CENTER_TAX_NUMBER)}:` +
                Mandatory_Character
              }
            >
              <Form.Control
                type="text"
                id="centerInfo.taxNumber"
                placeholder={t(translationConstants.ENTER_CENTER_TAX_NUMBER)}
                {...register("centerInfo.taxNumber", {
                  required: t(translationConstants.CENTER_TAX_NUMBER_REQUIRED),
                  pattern: {
                    value: Regex.fifteenDigitsOnlyRegex,
                    message: t(
                      translationConstants.CENTER_TAX_NUMBER_NUMERIC_ONLY
                    ),
                  },
                })}
              />
              {errors.centerInfo?.taxNumber && (
                <p className="errorText">
                  {errors.centerInfo?.taxNumber.message}
                </p>
              )}
            </InputFieldWrapper>

            <InputFieldWrapper
              label={`${t(translationConstants.WEBSITE_LINK)}:`}
            >
              <Form.Control
                type="text"
                id="centerInfo.websiteURL"
                placeholder={t(translationConstants.ENTER_WEBSITE_LINK)}
                {...register("centerInfo.websiteURL", {
                  pattern: {
                    value: Regex.urlRegex,
                    message: t(translationConstants.URL_VALIDATION),
                  },
                })}
              />
              {errors.centerInfo?.websiteURL && (
                <p className="errorText">
                  {errors.centerInfo?.websiteURL.message}
                </p>
              )}
            </InputFieldWrapper>

            <InputFieldWrapper
              label={
                `${t(translationConstants.CONTACT_EMAIL)}:` +
                Mandatory_Character
              }
            >
              <Form.Control
                {...register("centerInfo.email", {
                  required: t(translationConstants.emailRequired),
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{1,}$/i,
                    message: t(translationConstants.invalidEmail),
                  },
                })}
                id="centerInfo.email"
                type="centerInfo.email"
                placeholder="Ex: abc@example.com"
              />
              {errors.centerInfo?.email && (
                <p className="errorText">{errors.centerInfo?.email.message}</p>
              )}
            </InputFieldWrapper>

            <Col md={6} sm={12} className="mb-3">
              <Form.Label className="Form-labels">
                {t(translationConstants.CHOOSE_SPORTS_YOU_OFFER)}:
                {Mandatory_Character}
              </Form.Label>
              <div className="d-flex flex-wrap flex-lg-nowrap justify-content-between col-md-12 px-0">
                <Form.Group
                  className="d-flex flex-column justify-content-space-between"
                  controlId="formBasicCheckbox1"
                >
                  <Form.Check
                    className="offer"
                    type="checkbox"
                    value={sportConstants.SAILING}
                    label={<p>{t(translationConstants.sailing)} </p>}
                    {...register("sports", {
                      validate: atLeastOneSportChecked,
                    })}
                  />
                </Form.Group>
                <Form.Group
                  className="d-flex flex-column justify-content-space-between "
                  controlId="formBasicCheckbox2"
                >
                  <Form.Check
                    className="offer"
                    type="checkbox"
                    value={sportConstants.KITESURFING}
                    label={<p>{t(translationConstants.kitesurfing)} </p>}
                    // {...register("kitesurfing")}
                    {...register("sports", {
                      validate: atLeastOneSportChecked,
                    })}
                  />
                </Form.Group>
                <Form.Group
                  className="d-flex flex-column justify-content-space-between "
                  controlId="formBasicCheckbox3"
                >
                  <Form.Check
                    className="offer"
                    type="checkbox"
                    value={sportConstants.WINDSURFING}
                    label={<p>{t(translationConstants.windsurfing)} </p>}
                    // {...register("windsurfing")}
                    {...register("sports", {
                      validate: atLeastOneSportChecked,
                    })}
                  />
                </Form.Group>
                <Form.Group
                  className="d-flex flex-column justify-content-space-between "
                  controlId="formBasicCheckbox4"
                >
                  <Form.Check
                    className="offer"
                    type="checkbox"
                    value={sportConstants.WINGFOILING}
                    label={<p>{t(translationConstants.wingfoiling)} </p>}
                    // {...register("wingfoiling")}
                    {...register("sports", {
                      validate: atLeastOneSportChecked,
                    })}
                  />
                </Form.Group>
              </div>
              {errors.sports && (
                <p className="errorText">
                  {t(translationConstants.ATLEAST_ONE_SPORT)}
                </p>
              )}
            </Col>

            <InputFieldWrapper
              label={
                `${t(translationConstants.UPLOAD_CENTER_LOGO)}:` +
                Mandatory_Character
              }
            >
              <div className="filterUploader">
                <FileUploader
                  sm
                  single
                  isImage
                  defaultValue={formState?.logo}
                  {...register("logo", {
                    required: t(translationConstants.IMG_IS_REQUIRED),
                  })}
                  onFileUpload={(file) => handleFileUpload(file, "logo")}
                  setValidationErrorMessage={setImgValidationMessage}
                  isError={errors.logo || imgValidationMessage}
                />
                {errors.logo && !imgValidationMessage && (
                  <p className="errorText">{errors.logo.message}</p>
                )}
                {imgValidationMessage && (
                  <p className="errorText">{imgValidationMessage}</p>
                )}
              </div>
            </InputFieldWrapper>

            <InputFieldWrapper
              md={12}
              label={
                <>
                  {t(translationConstants.CENTER_DESCRIPTION)}:{" "}
                  <span className="greenColor">
                    ({t(translationConstants.ENGLISH_LANGUAGE)})
                  </span>
                </>
              }
            >
              <Form.Control
                type="text"
                id="centerInfo.description"
                as="textarea"
                rows={2}
                placeholder={t(translationConstants.ENTER_CENTER_DESCRIPTION)}
                {...register("centerInfo.description", {})}
              />
              {errors.centerInfo?.description && (
                <p className="errorText">
                  {errors.centerInfo?.description.message}
                </p>
              )}
            </InputFieldWrapper>

            <InputFieldWrapper
              md={12}
              label={
                <>
                  {t(translationConstants.CENTER_DESCRIPTION)}:{" "}
                  <span className="greenColor">
                    ({t(translationConstants.ARABIC)})
                  </span>
                </>
              }
            >
              <Form.Control
                dir={Directions.rtl}
                type="text"
                id="centerInfo.arabicDescription"
                as="textarea"
                rows={2}
                placeholder={"أدخل وصف المركز"}
                {...register("centerInfo.arabicDescription", {})}
              />
              {errors.centerInfo?.arabicDescription && (
                <p className="errorText">
                  {errors.centerInfo?.arabicDescription.message}
                </p>
              )}
            </InputFieldWrapper>

            <Col md={12} className="mb-3">
              <Form.Label className="Form-labels">
                {t(translationConstants.SOCIAL_MEDIA_LINKS)}:
              </Form.Label>
              <Row>
                <Col md={6} sm={12}>
                  <Form.Group
                    className="social-media-links"
                    controlId="socialMediaTwitterCheckboxes"
                  >
                    <Form.Check
                      type="checkbox"
                      className="custom-checkbox custom-align"
                      {...register(socialMediaConstants.TWITTER)}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        onSocialMediaChange(socialMediaConstants.TWITTER);
                        trigger("centerInfo.twitterURL").then(() => {
                          if (!isChecked) {
                            clearErrors("centerInfo.twitterURL");
                          }
                        });
                      }}
                      label={
                        <div className="row">
                          <div className="d-flex align-items-center col-3 col-md-4 col-lg-3">
                            <span>{t(translationConstants.TWITTER)}</span>
                          </div>
                          <div className="d-flex align-items-center col-9 col-md-8 col-lg-9">
                            <Form.Control
                              type="text"
                              disabled={
                                !socialMedia.includes(
                                  socialMediaConstants.TWITTER
                                )
                              }
                              {...register("centerInfo.twitterURL", {
                                validate: {
                                  validateTwitter: (field, formValues) =>
                                    !(formValues.twitter && !field) ||
                                    t(translationConstants.URL_REQUIRED),
                                },
                                pattern: {
                                  value: Regex.urlRegex,
                                  message: t(
                                    translationConstants.URL_VALIDATION
                                  ),
                                },
                              })}
                            />
                          </div>
                          {errors.centerInfo?.twitterURL && (
                            <p className="errorText ml-82 d-md-none d-flex d-lg-flex">
                              {errors.centerInfo.twitterURL.message}
                            </p>
                          )}
                          {errors.centerInfo?.twitterURL && (
                            <p className="errorText ml-55 d-none d-md-flex d-lg-none">
                              {errors.centerInfo.twitterURL.message}
                            </p>
                          )}
                        </div>
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6} sm={12}>
                  <Form.Group
                    className="social-media-links"
                    controlId="socialMediaInstagramCheckboxes"
                  >
                    <Form.Check
                      type="checkbox"
                      className="custom-checkbox custom-align"
                      {...register(socialMediaConstants.INSTAGRAM)}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        onSocialMediaChange(socialMediaConstants.INSTAGRAM);
                        trigger("centerInfo.instagramURL").then(() => {
                          if (!isChecked) {
                            clearErrors("centerInfo.instagramURL");
                          }
                        });
                      }}
                      label={
                        <div className="row">
                          <div className="d-flex align-items-center col-3 col-md-4 col-lg-3">
                            <span>{t(translationConstants.INSTAGRAM)}</span>
                          </div>
                          <div className="col-9 col-md-8 col-lg-9">
                            <Form.Control
                              type="text"
                              disabled={
                                !socialMedia.includes(
                                  socialMediaConstants.INSTAGRAM
                                )
                              }
                              {...register("centerInfo.instagramURL", {
                                validate: {
                                  validateInstagram: (field, formValues) =>
                                    !(formValues.instagram && !field) ||
                                    t(translationConstants.URL_REQUIRED),
                                },
                                pattern: {
                                  value: Regex.urlRegex,
                                  message: t(
                                    translationConstants.URL_VALIDATION
                                  ),
                                },
                              })}
                            />
                          </div>
                          {errors.centerInfo?.instagramURL && (
                            <p className="errorText ml-82 d-md-none d-flex d-lg-flex">
                              {errors.centerInfo?.instagramURL.message}
                            </p>
                          )}
                          {errors.centerInfo?.instagramURL && (
                            <p className="errorText ml-55 d-none d-md-flex d-lg-none">
                              {errors.centerInfo?.instagramURL.message}
                            </p>
                          )}
                        </div>
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6} sm={12}>
                  <Form.Group
                    className="social-media-links"
                    controlId="socialMediaFacebookCheckboxes"
                  >
                    <Form.Check
                      type="checkbox"
                      className="custom-checkbox custom-align"
                      {...register(socialMediaConstants.FACEBOOK)}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        onSocialMediaChange(socialMediaConstants.FACEBOOK);
                        trigger("centerInfo.facebookURL").then(() => {
                          if (!isChecked) {
                            clearErrors("centerInfo.facebookURL");
                          }
                        });
                      }}
                      label={
                        <div className="row">
                          <div className="d-flex align-items-center col-3 col-md-4 col-lg-3">
                            <span>{t(translationConstants.FACEBOOK)}</span>
                          </div>
                          <div className="col-9 col-md-8 col-lg-9">
                            <Form.Control
                              type="text"
                              disabled={
                                !socialMedia.includes(
                                  socialMediaConstants.FACEBOOK
                                )
                              }
                              {...register("centerInfo.facebookURL", {
                                validate: {
                                  validateFacebook: (field, formValues) =>
                                    !(formValues.facebook && !field) ||
                                    t(translationConstants.URL_REQUIRED),
                                },
                                pattern: {
                                  value: Regex.urlRegex,
                                  message: t(
                                    translationConstants.URL_VALIDATION
                                  ),
                                },
                              })}
                            />
                          </div>
                          {errors.centerInfo?.facebookURL && (
                            <p className="errorText ml-82 d-md-none d-flex d-lg-flex">
                              {errors.centerInfo?.facebookURL.message}
                            </p>
                          )}
                          {errors.centerInfo?.facebookURL && (
                            <p className="errorText ml-55 d-none d-md-flex d-lg-none">
                              {errors.centerInfo?.facebookURL.message}
                            </p>
                          )}
                        </div>
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6} sm={12}>
                  <Form.Group
                    className="social-media-links"
                    controlId="socialMediaTiktokCheckboxes"
                  >
                    <Form.Check
                      type="checkbox"
                      className="custom-checkbox custom-align"
                      {...register(socialMediaConstants.TIKTOK)}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        onSocialMediaChange(socialMediaConstants.TIKTOK);
                        trigger("centerInfo.tikTokURL").then(() => {
                          if (!isChecked) {
                            clearErrors("centerInfo.tikTokURL");
                          }
                        });
                      }}
                      label={
                        <div className="row">
                          <div className="d-flex align-items-center col-3 col-md-4 col-lg-3">
                            <span>{t(translationConstants.TIKTOK)}</span>
                          </div>
                          <div className="col-9 col-md-8 col-lg-9">
                            <Form.Control
                              type="text"
                              disabled={
                                !socialMedia.includes(
                                  socialMediaConstants.TIKTOK
                                )
                              }
                              {...register("centerInfo.tikTokURL", {
                                validate: {
                                  validateTiktok: (field, formValues) =>
                                    !(formValues.tiktok && !field) ||
                                    t(translationConstants.URL_REQUIRED),
                                },
                                pattern: {
                                  value: Regex.urlRegex,
                                  message: t(
                                    translationConstants.URL_VALIDATION
                                  ),
                                },
                              })}
                            />
                          </div>
                          {errors.centerInfo?.tikTokURL && (
                            <p className="errorText ml-82 d-md-none d-flex d-lg-flex">
                              {errors.centerInfo.tikTokURL.message}
                            </p>
                          )}
                          {errors.centerInfo?.tikTokURL && (
                            <p className="errorText ml-55 d-none d-md-flex d-lg-none">
                              {errors.centerInfo.tikTokURL.message}
                            </p>
                          )}
                        </div>
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Col>

            <Col md={12} className="mb-4">
              <p className="font-weight-700 font-16 text-dark">
                {t(translationConstants.LOGIN_DETAILS_HEADING)}
              </p>
              <hr className="horizontal-divider m-0" />
            </Col>

            <InputFieldWrapper
              label={`${t(translationConstants.Email)}` + Mandatory_Character}
            >
              <Form.Control
                type="text"
                id="userInfo.email"
                placeholder={t(translationConstants.emailPlaceholder)}
                {...register("userInfo.email", {
                  required: t(translationConstants.emailRequired),
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{1,}$/i,
                    message: t(translationConstants.invalidEmail),
                  },
                })}
              />
              {errors.userInfo?.email && (
                <p className="errorText">{errors.userInfo.email.message}</p>
              )}
            </InputFieldWrapper>

            <InputFieldWrapper
              label={t(translationConstants.password) + Mandatory_Character}
            >
              <Controller
                name="userInfo.password"
                control={control}
                rules={{
                  required: t(translationConstants.passwordRequired),
                  pattern: {
                    value:
                      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                    message: t(translationConstants.passwordValidation),
                  },
                }}
                render={({ field }) => (
                  <Password
                    placeholder={t(translationConstants.passwordPlaceholder)}
                    {...field}
                    toggleMask
                    feedback={false}
                  />
                )}
              />
              {errors.userInfo?.password && (
                <p className="errorText">{errors.userInfo.password.message}</p>
              )}
            </InputFieldWrapper>

            <InputFieldWrapper
              label={
                t(translationConstants.confirmPassword) + Mandatory_Character
              }
            >
              <Controller
                name="confirmPassword"
                control={control}
                rules={{
                  required: t(translationConstants.confirmPasswordRequired),
                  validate: (value) =>
                    value === getValues("userInfo.password") ||
                    t(translationConstants.passwordsDoNotMatch),
                }}
                render={({ field }) => (
                  <Password
                    placeholder={t(translationConstants.confirmPassword)}
                    {...field}
                    toggleMask
                    feedback={false}
                  />
                )}
              />
              {errors.confirmPassword && (
                <p className="errorText">{errors.confirmPassword.message}</p>
              )}
            </InputFieldWrapper>
          </Row>

          <Row>
            <Col md={12} className="d-flex justify-content-center">
              <div className="d-flex justify-content-center align-items-center">
                <Button
                  type="button"
                  onClick={goBack}
                  className="btn-submit-outline mx-2"
                >
                  {t(translationConstants.GO_BACK)}
                </Button>
                <Button type="submit" className="UpdateUserView-button mx-2">
                  {t(translationConstants.NEXT)}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </Form>
    </FormProvider>
  );
};

export default BasicTab;
